import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  InputGroup,
  InputGroupLabel,
  OptionType,
  TextInput,
} from '@fullstory/components';
import { ResourceSelectMenu } from '@fullstory/library-ui';
import { useIntl } from 'react-intl';

const protocol = 'https://';

export interface HostnameSelectProps {
  /** The current hostname value */
  value: string | undefined;
  /** The list of hostname options */
  options: string[];
  /** Callback that fires when the hostname is selected, or a custom one is submitted. */
  onBlur: (hostname: string | undefined) => void;
  /** Whether the current value is invalid */
  invalid: boolean;
  preventOverflow?: boolean;
}

export const HostnameSelect: FC<HostnameSelectProps> = ({
  value,
  options: rawOptions,
  onBlur,
  invalid,
  preventOverflow,
}) => {
  const options = useMemo(() => rawOptions.map(toOptionType), [rawOptions]);
  const selected = useMemo(() => (value ? toOptionType(value) : undefined), [value]);
  const intl = useIntl();
  const handleSelect = (option: OptionType<string> | null) => onBlur(option?.value);

  const [custom, setCustom] = useState(false); // If true, show the custom hostname input
  const showCustom = () => {
    onBlur('');
    setCustom(true);
  };
  const hideCustom = () => setCustom(false);

  const input = custom ? (
    <CustomHostnameInput
      value={value || ''}
      onBlur={onBlur}
      invalid={invalid}
      onClose={hideCustom}
    />
  ) : (
    <ResourceSelectMenu<string>
      itemType="hostname"
      variant="fill-builder"
      selected={selected}
      options={options}
      onChange={handleSelect}
      onCreate={showCustom}
      placeholder={intl.formatMessage({ defaultMessage: 'Select a hostname...', id: 'Ewktxh' })}
      filterPlaceholder={intl.formatMessage({ defaultMessage: 'Find a hostname...', id: 'lQtaNs' })}
      preventOverflow={preventOverflow ? 16 : undefined}
      flipY={preventOverflow || undefined}
    />
  );

  return (
    <Box maxWidth={340}>
      <InputGroup>
        <InputGroupLabel>{protocol}</InputGroupLabel>
        {input}
      </InputGroup>
    </Box>
  );
};

const toOptionType = (s: string) => ({ label: s, value: s, key: s });

interface CustomHostnameInputProps {
  value: string;
  onBlur: (value: string) => void;
  invalid: boolean;
  onClose: () => void;
}

const CustomHostnameInput: FC<CustomHostnameInputProps> = ({ value, onBlur, invalid, onClose }) => {
  const intl = useIntl();
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => ref.current?.focus(), [ref.current]);
  const [currValue, setCurrValue] = useState(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrValue(e.target.value);
  };

  const handleKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !invalid) {
      onClose();
    }
  };

  const handleClose = () => {
    onBlur('');
    onClose();
  };

  return (
    <TextInput
      ref={ref}
      value={currValue}
      onChange={handleChange}
      onKeyDown={handleKeydown}
      invalid={invalid}
      placeholder={intl.formatMessage({ defaultMessage: 'e.g. www.example.com', id: 'jWGZOf' })}
      rightAdornment={
        <Box paddingRight={8}>
          <IconButton
            title={intl.formatMessage({ defaultMessage: 'Remove custom hostname', id: 'glxGFT' })}
            iconName="close"
            onClick={handleClose}
          />
        </Box>
      }
      onBlur={() => onBlur(currValue)}
    />
  );
};
